import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any | null;
  error: Error | null;
  handleAuthCallback: (code: string) => Promise<void>;
  handleSignOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
  handleAuthCallback: async () => { throw new Error('Not implemented'); },
  handleSignOut: async () => { throw new Error('Not implemented'); },
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      setIsAuthenticated(!!session.tokens);
      setUser(user);
    } catch (err) {
      setIsAuthenticated(false);
      setUser(null);
      setError(err instanceof Error ? err : new Error('Authentication error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthCallback = async (code: string) => {
    try {
      setIsLoading(true);
      // This function will be called after redirect
      await signInWithRedirect();
      await checkAuthState();
    } catch (err) {
      console.error('Auth callback error:', err);
      setError(err instanceof Error ? err : new Error('Authentication error'));
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      await signOut();
      setIsAuthenticated(false);
      setUser(null);
      setError(null);
      window.location.href = '/'; // Redirect to home page after logout
    } catch (err) {
      console.error('Logout error:', err);
      setError(err instanceof Error ? err : new Error('Logout error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      user, 
      error, 
      handleAuthCallback,
      handleSignOut
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);